import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ExternalUserFile,
  externalUserFileModuleMap,
} from '@restore/ExternalUserSubjects/ExternalUserSubjects.types';
import * as api from '@api/externalUserSubjects';

interface UseVisitFilesParams {
  clinicalTrialId: string | undefined;
  patientId: string | undefined;
  patientCode: string | undefined;
  selectedVisit: string;
  visitName: string | undefined;
}

export const useVisitFiles = ({
  clinicalTrialId,
  patientId,
  patientCode,
  selectedVisit,
  visitName,
}: UseVisitFilesParams) => {
  const [allFiles, setAllFiles] = useState<ExternalUserFile[]>([]);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [downloadingFileIds, setDownloadingFileIds] = useState<Set<string>>(
    new Set()
  );

  const fetchFiles = useCallback(async () => {
    if (!patientId || !clinicalTrialId) return;

    setUploadsLoading(true);
    try {
      const patientFiles = await api.fetchExternalUserPatientFiles(
        clinicalTrialId,
        patientId
      );
      setAllFiles(patientFiles);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      setAllFiles([]);
    } finally {
      setUploadsLoading(false);
    }
  }, [patientId, clinicalTrialId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // Files of the currently selected visit (`file.visitId` is the visit instance id).
  const files = useMemo(
    () => allFiles.filter(file => file.visitId === selectedVisit),
    [allFiles, selectedVisit]
  );

  // Visit ids that have uploads — used to disable visit tabs without files.
  const visitIdsWithFiles = useMemo(
    () => new Set(allFiles.map(file => file.visitId)),
    [allFiles]
  );

  const handleDownloadFile = useCallback(
    async (fileId: string) => {
      if (!clinicalTrialId) return;

      setDownloadingFileIds(prev => new Set(prev).add(fileId));
      try {
        const downloadInfo = await api.getExternalUserFileDownload(
          clinicalTrialId,
          fileId
        );

        const file = files.find(f => f.id === fileId);

        let filename = 'Export.zip';
        if (file && patientCode && visitName) {
          const sanitize = (str: string) =>
            str
              .trim()
              .replace(/[^\p{L}\p{N}]+/gu, '_')
              .replace(/^_+|_+$/g, '');

          const sanitizedVisitName = sanitize(visitName);
          const source = externalUserFileModuleMap[file.module] ?? 'Export';
          const sanitizedSource = sanitize(source);
          filename = `${patientCode}_${sanitizedVisitName}_${sanitizedSource}_${file.dataType}.zip`;
        }

        const response = await fetch(downloadInfo.url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      } catch (error) {
        console.error('Failed to download file:', error);
      } finally {
        setDownloadingFileIds(prev => {
          const next = new Set(prev);
          next.delete(fileId);
          return next;
        });
      }
    },
    [clinicalTrialId, files, patientCode, visitName]
  );

  return {
    files,
    visitIdsWithFiles,
    uploadsLoading,
    handleDownloadFile,
    downloadingFileIds,
  };
};
